import React from 'react'
import {
  Section,
  Switch,
  Textfield,
  ProcessEditor,
  MultiField,
  ScoreEditor,
  OptionEditor,
  Color,
  Dropdown,
  Image,
  Radio,
  PlainTextEditor,
  Datastream,
  Form,
  Custom,
} from '../formFields'
import {
  eventType,
  webhookUrl,
  data,
  delay,
  toastMessage,
  toastVariant,
  alignment,
  verticalAlignment,
  direction,
} from './_common'
import DefaultDataset from 'components/_custom/DefaultDataset'
import {
  ACTION_FLOW_DEFAULT_VERTICAL,
  actionFlowAttributesSection,
  actionFlowVerticalField,
  getActionFlowVerticalPreset,
  getActionFlowStepsColumns,
} from './vertical'

// TODO: Factory like in web.js

const getBaseFields = (withHideTitle = true) => [
  ...(withHideTitle
    ? [
        Switch({
          name: 'hideTitle',
          label: 'Hide title',
          defaultChecked: false,
        }),
      ]
    : []),
  Textfield({ name: 'title', label: 'Title', hidden: (cmp) => cmp.content.hideTitle }),
  Textfield({ name: 'description', label: 'Description', hidden: (cmp) => cmp.content.hideTitle }),
]

// common reusable fields
const VibraniumOptionEditor = ({ columns, ...otherProps }) =>
  // @ts-ignore
  OptionEditor({
    ...otherProps,
    columns: columns.map((c) =>
      c.type !== 'icon'
        ? c
        : {
            ...c,
            options: [
              'car-side',
              'car',
              'cart-plus',
              'check-circle',
              'coins',
              'credit-card',
              'eye',
              'globe-africa',
              'globe-americas',
              'globe-asia',
              'globe-europe',
              'globe',
              'globe',
              'map-marker-alt',
              'map-marker',
              'map',
              'mobile',
              'money-bill-wave',
              'phone',
              'piggy-bank',
              'plane-arrival',
              'plane-departure',
              'plane-slash',
              'plane',
              'robot',
              'rocket',
              'search',
              'shopping-cart',
              'spinner',
              'star',
              'sticky-note',
              'sync',
              'times-circle',
              'user-plus',
              'wallet',
              'wrench',
            ],
          }
    ),
  })

// modules

export const activityTimeline = {
  name: 'activityTimeline',
  docs: 'activity-timeline',
  label: 'Activity Timeline',
  description: 'Displays experience events as cards in a timeline. Events can be filtered by channnels and types.',
  hasChildren: false,
  fields: [
    ...getBaseFields(),
    MultiField({
      name: 'sources',
      label: 'Channel',
    }),
    VibraniumOptionEditor({
      name: 'eventTypes',
      label: 'Event Types',
      columns: [
        {
          name: 'id',
          label: 'ID*',
        },
        { name: 'name', label: 'Name' },
        {
          name: 'titlePath',
          label: 'Title',
          tooltip:
            // eslint-disable-next-line no-template-curly-in-string
            'It can either be a path in an event object, a simple string or a combination of both, e.g. "Simple string ${path}"',
        },
        {
          name: 'imagePath',
          label: 'Image path',
          tooltip:
            '(Optional) Path in the event JSON object that points to the image of the event. If empty or not found, thumbnail will be used as fallback.',
        },
        {
          name: 'thumbnail',
          label: 'Thumbnail URL',
          tooltip: '(Optional) Icon will be used as fallback.',
        },
        {
          name: 'icon',
          label: 'Icon*',
          type: 'icon',
        },
        {
          name: 'customSidebarFields',
          label: 'Custom sidebar fields',
          tooltip:
            '(Optional) A JSON representing an object or an array of objects, each of which contains two properties: label and value, which specify the additional details to be presented in an event sidebar view.\n\nBoth label and value can be either a static string or a dynamic one. To specify a dynamic value provide a selector path for an event. You can do it either by simply writing the selector as a string or by surrounding it in ${} brackets. By doing the latter, in case of no occurrencies the returned value will be empty, by doing the former you will get your selector unchanged as a result.',
        },
      ],
    }),
  ],
  defaultContent: {
    meta: { component: 'activityTimeline' },
    content: {
      hideTitle: false,
      title: 'Customer Touchpoints',
      description: 'Displays experience events as cards in a timeline. Events can be filtered by channnels and types.',
      eventTypes: [
        {
          id: 'commerce.productViews',
          name: 'Product viewed',
          titlePath: 'entity.productListItems[0].name',
          imagePath: 'entity.productListItems[0].$tenantId.core.imageURL',
          thumbnail: '',
          icon: 'search',
        },
        {
          id: 'commerce.productListAdds',
          name: 'Added to cart',
          titlePath: 'entity.productListItems[0].name',
          imagePath: 'entity.productListItems[0].$tenantId.core.imageURL',
          thumbnail: '',
          icon: 'cart-plus',
        },
        {
          id: 'commerce.purchases',
          name: 'Purchased',
          titlePath: 'entity.productListItems[0].name',
          imagePath: 'entity.productListItems[0].$tenantId.core.imageURL',
          thumbnail: '',
          icon: 'credit-card',
        },
        {
          id: 'offer.declined',
          name: 'Offer declined',
          titlePath: 'entity.$tenantId.proposition.name',
          imagePath: 'entity.$tenantId.proposition.image',
          thumbnail: '',
          icon: 'times-circle',
        },
        {
          id: 'offer.accepted',
          name: 'Offer accepted',
          titlePath: 'entity.$tenantId.proposition.name',
          imagePath: 'entity.$tenantId.proposition.image',
          thumbnail: '',
          icon: 'check-cirle',
        },
        // {
        //   id: 'orderTracking',
        //   name: 'Call satisfaction',
        //   imagePath: '',
        //   thumbnail: '',
        //   icon: '',
        // },
        {
          id: 'rateCall',
          name: 'Call rated',
          titlePath: 'entity.$tenantId].interactionDetails.core.callCenterAgent.callTopic',
          imagePath: '',
          thumbnail: '',
          icon: 'star',
        },
        {
          id: 'geofencing.entry',
          name: 'Geofencing entry',
          titlePath: 'entity.placeContext.POIinteraction.poiDetail.name',
          imagePath: '',
          thumbnail: '',
          icon: 'location-plus',
        },
        {
          id: 'geofencing.exit',
          name: 'Geofencing exit',
          titlePath: 'entity.placeContext.POIinteraction.poiDetail.name',
          imagePath: '',
          thumbnail: '',
          icon: 'location-minus',
        },
        {
          id: 'account.registration',
          name: 'Account registration',
          titlePath: 'entity.$tenantId.identification.core.email',
          imagePath: '',
          thumbnail: '',
          icon: 'user-plus',
        },
        {
          id: 'Bot.interaction',
          name: 'Chatbot interaction',
          titlePath: 'entity.$tenantId.interactionDetails.core.bot.botTopic',
          imagePath: '',
          thumbnail: '',
          icon: 'robot',
        },
        {
          id: 'web.webpagedetails.pageViews',
          name: 'Page view',
          titlePath: 'entity.web.webPageDetails.name',
          imagePath: 'image',
          thumbnail: '',
          icon: 'eye',
        },
      ],
      sources: ['web', 'mobile', 'call-center', 'crm', 'cx', 'ar', 'chatbot', 'iot'],
    },
  },
}

export const processFlow = {
  name: 'processFlow',
  docs: 'process-flow',
  label: 'Process Flow',
  description: 'Provides a visual representation of a business process and moves a profile across its different steps.',
  hasChildren: false,
  fields: [...getBaseFields(), ProcessEditor({ name: 'steps', label: 'Steps' })],
  defaultContent: {
    meta: { component: 'processFlow' },
    content: {
      hideTitle: false,
      title: 'Process Flow',
      description:
        'Provides a visual representation of a business process and moves a profile across its different steps',
      steps: [
        {
          description: 'Prospect recently onboarded',
          eventType: 'leadOperation.newLead',
          step: 'Lead Generation',
        },
        {
          description: 'Prospect looking for info',
          eventType: 'leadOperation.qualify',
          step: 'Qualify Lead',
        },
        {
          description: 'Develop personalized needs',
          eventType: 'leadOperation.develop',
          step: 'Develop Solution',
        },
        {
          description: 'Develop proposal & complete internal review',
          eventType: 'leadOperation.propose',
          step: 'Develop Proposal',
        },
        {
          description: 'Complete Final Proposal',
          eventType: 'leadOperation.close',
          step: 'Complete Process',
        },
      ],
    },
  },
}

export const actionFlow = {
  name: 'actionFlow',
  docs: 'action-flow',
  label: 'Live Activities',
  description:
    'A guided, step-by-step checklist for working with a customer, where each step you take can trigger the right system actions (events, updates, or handoffs) the business has set up.',
  hasChildren: false,
  fields: [
    ...getBaseFields(),
    Textfield({
      name: 'campaignId',
      label: 'Campaign ID',
    }),
    actionFlowVerticalField,
    actionFlowAttributesSection,
    ProcessEditor({
      name: 'steps',
      label: 'Steps',
      columns: getActionFlowStepsColumns,
      emptyRow: { contentState: {} },
    }),
  ],
  defaultContent: {
    meta: { component: 'actionFlow' },
    content: {
      hideTitle: false,
      title: 'Live Activities',
      description:
        'A guided, step-by-step checklist for working with a customer, where each step you take can trigger the right system actions (events, updates, or handoffs) the business has set up.',
      vertical: ACTION_FLOW_DEFAULT_VERTICAL,
      ...getActionFlowVerticalPreset(ACTION_FLOW_DEFAULT_VERTICAL),
    },
  },
}

export const contact = {
  name: 'contact',
  docs: 'contact',
  label: 'Contact',
  description: 'Displays a form to contact customer using email, sms or phone. Triggers customEvent in Launch',
  hasChildren: false,
  fields: [
    ...getBaseFields(),
    Section('Email', Switch({ name: 'emailEnabled', label: 'Enabled', defaultChecked: true })),
    Section('SMS', Switch({ name: 'smsEnabled', label: 'Enabled', defaultChecked: true })),
    Section(
      'Phone',
      Switch({ name: 'phoneEnabled', label: 'Enabled', defaultChecked: true }),
      Switch({
        name: 'showLogCallSection',
        label: 'Show log call section',
        defaultChecked: true,
        hidden: (cmp) => !cmp.content.phoneEnabled,
      }),
      MultiField({
        name: 'callTopics',
        label: 'Call topics',
        hidden: (cmp) => !cmp.content.phoneEnabled && !cmp.content.showLogCallSection,
      })
    ),
  ],
  defaultContent: {
    meta: { component: 'contact' },
    content: {
      hideTitle: false,
      title: 'Contact Customer',
      description: 'Send message',
      emailEnabled: true,
      smsEnabled: true,
      phoneEnabled: true,
      showLogCallSection: true,
      callTopics: ['Promotion', 'Invoice questions', 'Complaint', 'Refunds'],
    },
  },
}

export const survey = {
  name: 'survey',
  docs: 'survey',
  label: 'Survey',
  hasChildren: false,
  description:
    'Displays questions to get customer feedbacks about products/services. Questions are rated from Strongly Disagree to Strongly Agree. Triggers customEvent in Launch',
  fields: [
    ...getBaseFields(),
    MultiField({
      name: 'questions',
      label: 'Questions',
    }),
  ],
  defaultContent: {
    meta: { component: 'survey' },
    content: {
      hideTitle: false,
      title: 'Survey',
      description: 'Get inputs from your customer and updates their satisfaction score.',
      questions: [
        'The app/website is appropriate for my shopping needs',
        'Merchandise sold is of highest quality',
        'The merchandise sold is good value for the money',
        'Advertised merchandise in stock',
        'I am very satisfied with the merchandise I purchased',
        'I would recommend this app/website for my friend and family',
      ],
    },
  },
}

export const offers = {
  name: 'offers',
  docs: '_K43-offers',
  label: 'Offers',
  description: 'Displays offers recommendation for the customer',
  hasChildren: false,
  fields: [
    ...getBaseFields(),
    Switch({
      name: 'useEncodedDecisionScopes',
      label: 'Use encoded decision scopes',
      defaultChecked: true,
    }),
    VibraniumOptionEditor({
      hidden: (cmp) => cmp.content.useEncodedDecisionScopes,
      name: 'decisionScopes',
      label: 'Decision scopes',
      columns: [
        {
          label: 'Decision Scope',
          name: 'decisionScope',
          tooltip: 'A JSON object containing at least xdm:placementId and xdm:activityId',
        },
        {
          label: 'Item count',
          name: 'itemCount',
        },
        {
          label: 'Hide texts',
          tooltip: 'Useful when the offer picture already contains texts and you want to hide the offer title',
          type: 'boolean',
          name: 'hideTexts',
        },
      ],
    }),
    VibraniumOptionEditor({
      hidden: (cmp) => !cmp.content.useEncodedDecisionScopes,
      name: 'encodedDecisionScopes',
      label: 'Encoded Decision scopes',
      columns: [
        {
          label: 'Encoded decision Scope',
          name: 'decisionScope',
        },
        {
          name: 'itemCount',
          label: 'Item count',
        },
        {
          label: 'Hide texts',
          tooltip: 'Useful when the offer picture already contains texts and you want to hide the offer title',
          type: 'boolean',
          name: 'hideTexts',
        },
      ],
    }),
    Dropdown({
      name: 'imageFit',
      label: 'Image fit',
      tooltip:
        'For offers containing a background image - defined whether the image should be contained or cover the entire area of the container',
      options: [
        { label: 'Cover', value: 'cover' },
        { label: 'Contain', value: 'contain' },
      ],
      filter: null,
    }),
    Color({
      name: 'textColor',
      label: 'Text color',
      tooltip: "Optional: Use if your primary color is not clearly visible on the offer's background",
    }),
    Textfield({
      name: 'height',
      label: 'Height',
      tooltip: 'Should have a unit recognizable by the CSS engine',
    }),
  ],
  defaultContent: {
    meta: { component: 'offers' },
    content: {
      hideTitle: false,
      title: 'Recommended offers',
      description: 'Offers recommendation for the customer',
      decisionScopes: [],
      encodedDecisionScopes: [],
      useEncodedDecisionScopes: false,
      imageFit: 'cover',
    },
  },
}

export const experienceDecisioning = {
  name: 'experienceDecisioning',
  docs: '',
  label: 'Experience Decisioning',
  description: 'Displays experience decisioning for the customer',
  hasChildren: false,
  fields: [
    ...getBaseFields(),
    Textfield({
      name: 'surface',
      label: 'Surface',
    }),
    Section(
      'Appearance',
      Dropdown({
        name: 'imageVariant',
        label: 'Image variant',
        options: [
          { label: 'Thumbnail', value: 'thumbnail' },
          { label: 'Background', value: 'background' },
        ],
        filter: null,
      }),
      Color({
        name: 'textColor',
        label: 'Text color',
        hidden: (cmp) => cmp.content.imageVariant === 'thumbnail',
        tooltip: "Use if the default text color is not clearly visible on the event image's background",
      }),
      direction,
      alignment,
      verticalAlignment
    ),
  ],
  defaultContent: {
    meta: { component: 'experienceDecisioning' },
    content: {
      hideTitle: false,
      title: 'Experience Decisioning',
      description: 'Experience decisioning recommendations for the customer',
      surface: '',
      imageVariant: 'thumbnail',
    },
  },
}

export const eventButton = {
  name: 'eventButton',
  docs: 'button',
  label: 'Button',
  description:
    'Displays a button with specified label and visual variant. Can trigger launch events or call a request to a webhook.',
  hasChildren: false,
  fields: [
    ...getBaseFields(),
    Textfield({ name: 'label', label: 'Button label' }),
    Section(
      'Appearance',
      Dropdown({
        name: 'variant',
        label: 'Variant',
        options: [
          { label: 'Accent', value: 'accent' },
          { label: 'Primary', value: 'primary' },
          { label: 'Secondary', value: 'secondary' },
          { label: 'Negative', value: 'negative' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'type',
        label: 'Type',
        options: [
          { label: 'Fill', value: 'fill' },
          { label: 'Outline', value: 'outline' },
        ],
        filter: null,
      }),
      alignment
    ),
    Section(
      'Actions',
      eventType,
      { ...webhookUrl, tooltip: 'URL to send a request to on click. Request will contain all data gathered on site.' },
      data,
      delay,
      {
        ...toastVariant,
        options: [
          { label: '<None>', value: '' },
          { label: 'Neutral', value: 'neutral' },
          { label: 'Positive', value: 'positive' },
          { label: 'Negative', value: 'negative' },
          { label: 'Info', value: 'info' },
        ],
      },
      {
        ...toastMessage,
        hidden: (cmp) => cmp.content.toastVariant === '',
      }
    ),
  ],
  defaultContent: {
    meta: { component: 'eventButton' },
    content: {
      hideTitle: true,
      title: 'Button',
      description: 'A button triggering launch events or calling a request to a webhook.',
      alignment: 'left',
      label: 'Click me',
      variant: 'primary',
      type: 'fill',
      toastVariant: 'neutral',
      toastMessage: 'Event triggered',
      delay: 0,
      eventType: '',
    },
  },
}

export const eventTrigger = {
  name: 'eventTrigger',
  docs: 'event-trigger',
  label: 'Event Trigger',
  description: 'Displays configured event details with a button to trigger it.',
  hasChildren: false,
  fields: [
    ...getBaseFields(false),
    Section(
      'Configuration',
      Datastream({ name: 'datastream', label: 'Datastream' }),
      Radio({
        name: 'mode',
        label: 'Configuration type',
        options: [
          { label: 'Simplified', value: 'simplified', defaultChecked: true },
          { label: 'Advanced', value: 'advanced' },
        ],
      }),
      Textfield({
        name: 'eventName',
        label: 'Event name',
        hidden: (cmp) => cmp.content.mode === 'advanced',
      }),
      PlainTextEditor({ name: 'payload', label: 'Payload', hidden: (cmp) => cmp.content.mode !== 'advanced' }),
      Switch({
        name: 'joTestModeEnabled',
        label: 'AJO Test Mode',
        tooltip: 'Adds a joTestModeEvent_ prefix to the event ID to allow the application to work with AJO Test Mode',
        defaultChecked: false,
      })
    ),
    Section(
      'Appearance',
      Textfield({ name: 'buttonLabel', label: 'Button label' }),
      Image({ name: 'image', label: 'Image' }),
      Dropdown({
        name: 'imageVariant',
        label: 'Image variant',
        options: [
          { label: 'Thumbnail', value: 'thumbnail' },
          { label: 'Background', value: 'background' },
        ],
        filter: null,
      }),
      Color({
        name: 'textColor',
        label: 'Text color',
        hidden: (cmp) => cmp.content.imageVariant === 'thumbnail',
        tooltip: "Use if the default text color is not clearly visible on the event image's background",
      }),
      direction,
      alignment,
      verticalAlignment
    ),
  ],
  defaultContent: {
    meta: { component: 'eventTrigger', treatTitleAsContent: true },
    content: {
      title: 'Event Trigger',
      description: 'Click the button to trigger an event.',
      mode: 'simplified',
      imageVariant: 'thumbnail',
      direction: 'vertical',
      alignment: 'left',
      verticalAlignment: 'middle',
      joTestModeEnabled: false,
      buttonLabel: 'Trigger event',
      payload: `
{
  "event": {
    "xdm": {
      "eventType": "",
      "$tenantId": {
        "interactionDetails": { "core": { "channel": "cx" } },
        "brandName": "$brandName"
      }
    },
    "data": {}
  }
}
      `,
    },
  },
}

export const profileUpdate = {
  name: 'profileUpdate',
  docs: 'update-profile',
  label: 'Update Profile',
  description: 'Displays a form which let you update Real Time Customer Profile attributes',
  fields: [
    ...getBaseFields(false),
    Form({ name: 'attributes', label: 'Attributes' }),
    Radio({
      name: 'datasetType',
      label: 'Dataset type',
      tooltip:
        'By default, the module will use "Profile Dataset for Customer Experience App", if available. You can also use a custom dataset.',
      options: [
        { label: 'Default', value: 'default', defaultChecked: true },
        { label: 'Custom', value: 'custom' },
      ],
    }),
    Textfield({
      name: 'customDatasetId',
      label: 'Custom Dataset ID',
      hidden: (cmp) => cmp.content.datasetType === 'default',
    }),
    Custom({
      name: 'defaultDataset',
      isComponent: true,
      render: () => <DefaultDataset datasetName='Profile Dataset for Customer Experience App' />,
      hidden: (cmp) => cmp.content.datasetType === 'custom',
    }),
  ],
  defaultContent: {
    meta: { component: 'profileUpdate', treatTitleAsContent: true },
    content: {
      title: 'Update Profile',
      description: 'Edit the fields to update the profile attributes',
      attributes: [
        {
          name: 'entity.person.name.firstName',
          type: 'text',
          label: 'First name',
        },
        {
          name: 'entity.person.name.lastName',
          type: 'text',
          label: 'Last name',
        },
        {
          name: 'entity.person.gender',
          type: 'dropdown',
          label: 'Gender',
          options: 'male,female,not_specified,non_specific',
        },
        {
          name: 'entity.testProfile',
          type: 'checkbox',
          label: 'Test Profile',
        },
      ],
      datasetType: 'default',
      customDatasetId: '',
    },
  },
}

export const customerAI = {
  name: 'customerAI',
  docs: 'customer-scores',
  label: 'Customer Scores',
  hasChildren: false,
  description:
    'Displays customer scores in radial graphs. Score can be configured as dynamic values from the Real Time Customer Profile or as constants.',
  fields: [...getBaseFields(), ScoreEditor({ name: 'scores', label: 'Scores' })],
  defaultContent: {
    meta: { component: 'customerAI' },
    content: {
      title: 'Customer Scoring',
      description: 'Displays scores attached to the customer.',
      scores: [
        {
          title: 'Churn Score',
          risk: 'red',
          attribute: 'entity.$tenantId.scoring.churn.churnPrediction',
          description: 'Churn Score description',
        },
        {
          title: 'Propensity Score',
          risk: 'green',
          attribute: 'entity.$tenantId.scoring.core.propensityScore',
          description: 'Churn Score description',
        },
        {
          title: 'RFM Score',
          risk: 'green',
          attribute: '0.20',
          max: '1',
          description: 'Churn Score description',
        },
      ],
    },
  },
}

export const DEFAULT_COMPONENT = activityTimeline.defaultContent
